import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import Lightbox from '../components/Lightbox';
import { GALLERY_ITEMS, COMPANY_INFO } from '../data/siteData';

export default function ReferencesPage() {
  const [activeCategory, setActiveCategory] = useState('Alle');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const categories = ['Alle', 'Træfældning', 'Topkapning', 'Stubfræsning', 'Beskæring', 'Hækklipning', 'Skov & Bane'];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [dropdownOpen]);

  const filteredImages =
    activeCategory === 'Alle'
      ? GALLERY_ITEMS
      : GALLERY_ITEMS.filter((item) => item.category === activeCategory);

  const openLightbox = (index) => {
    setActiveImageIndex(index);
    setLightboxOpen(true);
  };

  const handleNext = () => {
    setActiveImageIndex((prev) => (prev + 1) % filteredImages.length);
  };

  const handlePrev = () => {
    setActiveImageIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
  };

  return (
    <>
      <SEO
        title="Referencer & Galleri - Hærvejens Træpleje"
        description="Se billeder af vores arbejde med træfældning, topkapning, rodfræsning og træbeskæring i hele Østjylland og Trekantområdet."
        canonical="https://xn--hrvejenstrpleje-xlbj.dk/referencer/"
      />

      <Breadcrumbs items={[{ label: 'Referencer & Galleri' }]} />

      {/* Header */}
      <section className="bg-[#10271c] text-white py-14 sm:py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs font-bold text-[#7dd2a6] bg-[#1a3d2c] px-3.5 py-1.5 rounded-full inline-block mb-4">
            Vores arbejde
          </span>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4">
            Referencer & Galleri
          </h1>
          <p className="text-base sm:text-lg text-[#cadcd1] leading-relaxed max-w-2xl mx-auto">
            Gå på opdagelse i vores galleri og se autentiske billeder fra vores arbejde med træfældning, topkapning, stubfræsning og træpleje i private haver og virksomheder.
          </p>
        </div>
      </section>

      {/* Category filter tabs */}
      <section className="py-6 bg-[#f5f8f6] border-b border-[#dce5df]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Dropdown menu until 1200px */}
          <div ref={dropdownRef} className="relative block min-[1200px]:hidden max-w-sm sm:max-w-md mx-auto">
            <button
              type="button"
              onClick={() => setDropdownOpen((prev) => !prev)}
              aria-expanded={dropdownOpen}
              aria-haspopup="listbox"
              className="w-full flex items-center justify-between px-4 py-2.5 sm:py-3 bg-white border border-[#cfddd4] rounded-xl text-xs sm:text-sm font-semibold text-[#14291d] shadow-2xs hover:border-[#2d6a4f] focus:outline-none focus:ring-2 focus:ring-[#2d6a4f]/25 transition-all cursor-pointer"
            >
              <span className="flex items-center gap-2 text-[#2d6a4f]">
                <i className="fa-solid fa-filter text-xs text-[#527a65]" aria-hidden="true" />
                <span className="text-[#64746d] font-normal text-xs">Kategori:</span>
                <span className="text-[#14291d] font-bold">{activeCategory}</span>
              </span>
              <i
                className={`fa-solid fa-chevron-down text-xs text-[#527a65] transition-transform duration-200 ${
                  dropdownOpen ? 'rotate-180' : ''
                }`}
                aria-hidden="true"
              />
            </button>

            {dropdownOpen && (
              <div
                role="listbox"
                className="absolute left-0 right-0 top-full mt-2 z-30 bg-white border border-[#cfddd4] rounded-xl shadow-lg p-1.5 space-y-1 overflow-hidden"
              >
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    role="option"
                    aria-selected={activeCategory === cat}
                    onClick={() => {
                      setActiveCategory(cat);
                      setDropdownOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-3.5 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors cursor-pointer text-left ${
                      activeCategory === cat
                        ? 'bg-[#1b4332] text-white shadow-2xs'
                        : 'text-[#34453a] hover:bg-[#eaf2ed]'
                    }`}
                  >
                    <span>{cat}</span>
                    {activeCategory === cat && (
                      <i className="fa-solid fa-check text-xs text-[#7dd2a6]" aria-hidden="true" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Horizontal buttons from 1200px and up */}
          <div className="hidden min-[1200px]:flex items-center justify-center gap-2 text-sm font-semibold">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-xl transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-[#1b4332] text-white shadow-2xs'
                    : 'bg-white border border-[#cfddd4] text-[#34453a] hover:bg-[#eaf2ed]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-14 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {filteredImages.map((item, index) => (
            <div
              key={index}
              onClick={() => openLightbox(index)}
              className="group relative rounded-2xl overflow-hidden border border-[#dce5df] bg-white shadow-2xs hover:shadow-md transition-all cursor-pointer flex flex-col"
            >
              <div className="relative aspect-4/3 overflow-hidden bg-[#e4ece7]">
                <img
                  src={item.src}
                  alt={item.alt || item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-white/90 text-[#1b4332] flex items-center justify-center text-base opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                    <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                  </div>
                </div>
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-[#163627]/85 text-white text-[11px] font-semibold backdrop-blur-xs">
                  {item.category}
                </div>
              </div>

              <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between">
                <h3 className="font-bold text-sm sm:text-base text-[#14291d] group-hover:text-[#2d6a4f] transition-colors">
                  {item.title}
                </h3>
                <span className="text-xs text-[#527a65] font-medium mt-2 flex items-center gap-1.5">
                  <i className="fa-solid fa-expand text-[10px]" aria-hidden="true" />
                  <span>Klik for at forstørre</span>
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Lightbox modal with touch gestures and keyboard support */}
      <Lightbox
        images={filteredImages}
        currentIndex={activeImageIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNext={handleNext}
        onPrev={handlePrev}
      />
    </>
  );
}
